import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { API_URL } from '@/config/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import DeliveryDailyActivity from './DeliveryDailyActivity';
import DeliveryMonthlyUsage from './DeliveryMonthlyUsage';
import { REFRESH_MS, SummaryTile, csvText, download, fmt, getJson } from './OrgContentDeliveryShared';

// ─── Types (backend: services/OrgContentDeliveryService.js) ───────────────────

// Two populations, never mixed in one column (PRD delivery-funnel rev. 3.1):
//   catalog      in_feed, eligible_not_generated — the current feed under the site's configured filter;
//                null when the site's feed coverage is not measured
//   our records  records_* — every canonical product our pages reach, whatever the feed or manifest says;
//                Live (records_live_*) is null when the site's manifest is not measured, Ready when the site
//                has no target language
// The rev. 2.1 keys (total, generated, approved, translated, published_latest, published_any,
// published_not_in_feed) are still sent for one release, for tabs opened before the deploy; this page
// does not read them.
interface Counts {
  in_feed: number | null;
  eligible_not_generated?: number | null;
  records_generated: number;
  records_approved: number;
  records_ready: number | null;
  records_live_latest: number | null;
  records_live_any: number | null;
  records_ready_not_live_latest: number | null; // |Ready \ Live, latest|, a set difference by product
  published_without_page?: number; // listed by the client's manifest, never generated by us
  published_not_traced: number;
  manifest_groups?: number; // item groups in the current manifest file (unavailable on older backends)
  manifest_sources?: number; // manifest configs read within a day of the site's latest successful read
  manifest_stale_sources?: number; // configs read successfully once but not within that day: ignored
  manifest_lines_without_id?: number; // lines of that file with no item group and no product id
  records_generated_since?: number;
  records_approved_since?: number;
}

// The organisation total adds the bases of its Live percentages: the same sites as its Live figures.
interface TotalCounts extends Counts {
  records_live_sites?: number; // delivery-on sites whose manifest is measured
  records_live_base_generated?: number; // Generated of those sites
  records_live_base_ready?: number; // Ready of those sites
}

interface SiteRow extends Counts {
  site_id: string;
  site_name: string;
  domain: string | null;
  status: string | null;
  delivery_active: boolean; // false: no target language, so no content is delivered
  target_language_count: number;
  canonical_mode: boolean;
  manifest_read_at?: string | null; // when the current manifest file was read; null: never
  feed_imported_at?: string | null; // when the latest successful feed import completed; null: none (nothing in the feed)
  // 'not_measured': an active config reads a non-file source (shopify_api), its latest import did
  // not stamp every product (incomplete_import), or a config's feed filter cannot be judged
  // (unsupported_feed_filter:<config id>): the imports cannot tell what the feed contains.
  // The catalog columns (In the feed, Eligible) are then null; the records columns are still counted.
  feed_coverage?: 'measured' | 'not_measured';
  feed_unmeasured_sources?: string[];
  manifest_measured?: boolean; // false: the Live columns are null, for manifest_unavailable_reason
  manifest_unavailable_reason?: string | null;
  records_ready_unavailable_reason?: string | null; // 'no_target_languages'
  // The latest fetch attempt after that read that did not succeed (failed, or never finished):
  // the lines it added before failing are not counted until the next successful read.
  manifest_later_attempt?: { status: string; started_at: string } | null;
}

// What the "Live" columns can say for a site: read from the current manifest file, or not
// available — and why. Never a zero that could be read as "nothing is live".
export type ManifestState =
  | { kind: 'read'; readAt: string }
  // read, then another attempt that is still running, failed or overlapped the read: a note under the
  // site, not a gate — the Live columns keep the read's figures (delivery-funnel PRD rev. 4)
  | { kind: 'later_attempt'; readAt: string; status: string; at: string }
  | { kind: 'none' } // no manifest file read yet (no config, or no successful fetch)
  | { kind: 'no_id'; lines: number } // the file was read, but its lines carry no product id
  | { kind: 'unknown' }; // the server did not report it (older backend)

export function manifestState(row: Partial<SiteRow>): ManifestState {
  if (row.manifest_read_at === undefined) return { kind: 'unknown' };
  if (row.manifest_read_at === null) return { kind: 'none' };
  if ((row.manifest_groups ?? 0) === 0 && (row.manifest_lines_without_id ?? 0) > 0) {
    return { kind: 'no_id', lines: row.manifest_lines_without_id ?? 0 };
  }
  const later = row.manifest_later_attempt;
  return later
    ? { kind: 'later_attempt', readAt: row.manifest_read_at, status: later.status, at: later.started_at }
    : { kind: 'read', readAt: row.manifest_read_at };
}

const LIVE_KEYS: ReadonlyArray<keyof Counts> = ['records_live_latest', 'records_live_any', 'records_ready_not_live_latest', 'published_not_traced'];
const CATALOG_KEYS: ReadonlyArray<keyof Counts> = ['in_feed', 'eligible_not_generated'];
// Only "no successful read" and "lines without a product id" blank the Live columns (rev. 4): a later
// attempt is treated exactly as 'read' by the columns, the percentages, the Total's coverage and the CSV.
const isLiveUnavailable = (state: ManifestState) => state.kind === 'none' || state.kind === 'no_id';
export function liveUnavailableTitle(state: ManifestState): string {
  switch (state.kind) {
    case 'none': return 'No manifest file read yet: not available, not zero';
    case 'no_id': return 'The manifest lines carry no product id, nothing can be matched: not available, not zero';
    default: return '';
  }
}

const utcShort = (iso: string) =>
  `${new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC`;

// A read file and another attempt that did not finish, failed or overlapped it: said under the site, with
// the read date above it. A note, not a gate (rev. 4): the Live columns use that read's lines, which the
// other run may have re-listed or re-dated (the records are mutable; no per-fetch membership), so the
// note never presents the figures as a snapshot of the successful read.
export function laterAttemptLabel(state: ManifestState): string {
  if (state.kind !== 'later_attempt') return '';
  const when = utcShort(state.at);
  const what = state.status === 'running' ? `another read started ${when} has not finished`
    : state.status === 'success' ? `another read started ${when} overlapped it`
    : `another read started ${when} failed`;
  return `${what}: the figures use the read of ${utcShort(state.readAt)} and may include lines that run changed`;
}

// What the Total's "Live" columns cover: a site whose manifest cannot be read contributes
// nothing to them — neither to the three Live figures nor to their percentage base. The page
// recomputes the Live figures of a partial Total from the covered sites alone; with no covered
// site they are unavailable. A blank Total would hide the sites that are known, so the figures
// stay, marked partial, on the screen and in the CSV.
export interface LiveCoverage {
  sites: number; covered: number; unavailable: string[];
  base: { records_generated: number; records_ready: number }; // the covered sites' bases of the Live percentages
  // "Published, not traced" is a LEGACY key: the server nulls it for a site whose feed is not measured, even when
  // the site's manifest is. Only known values are summed; with none it is null (unavailable), never 0, and
  // notTracedMissing names the covered sites it leaves out (codex x2, #7936).
  counts: { published_not_traced: number | null };
  notTracedMissing: string[];
}
// Live depends on the manifest only, never on the feed (R3.3): every delivery-on site counts, and a site's
// Live figures are unavailable exactly when its manifest is (the server's manifest_measured, the same rule).
const liveUnavailableSite = (s: Partial<SiteRow>) => s.manifest_measured === false || isLiveUnavailable(manifestState(s));
export function liveCoverage(sites: Partial<SiteRow>[]): LiveCoverage {
  const unavailable = sites.filter(liveUnavailableSite);
  const covered = sites.filter((s) => !liveUnavailableSite(s));
  const sum = (k: keyof Counts) => covered.reduce((n, s) => n + Number(s[k] ?? 0), 0);
  const known = (s: Partial<SiteRow>) => s.published_not_traced !== null && s.published_not_traced !== undefined;
  const traced = covered.filter(known);
  return { sites: sites.length, covered: covered.length, unavailable: unavailable.map((s) => s.site_name ?? ''),
    base: { records_generated: sum('records_generated'), records_ready: sum('records_ready') },
    counts: { published_not_traced: traced.length === 0 ? null : traced.reduce((n, s) => n + Number(s.published_not_traced), 0) },
    notTracedMissing: covered.filter((s) => !known(s)).map((s) => s.site_name ?? '') };
}
export function coverageLabel(c: LiveCoverage): string {
  const live = c.unavailable.length === 0 ? '' : `Live columns cover ${c.covered} of ${c.sites} sites: ${c.unavailable.join(', ')} unavailable`;
  // Whenever a covered site's count is unknown, with or without an unreadable manifest elsewhere (codex x2, #7936).
  const notTraced = c.notTracedMissing.length > 0
    ? `“Published, not traced” covers ${c.covered - c.notTracedMissing.length} of ${c.sites} sites: ${c.notTracedMissing.join(', ')} feed not measured`
    : '';
  return [live, notTraced].filter(Boolean).join('; ');
}
// The Total as the page shows it: the server's sums, with the Live figures of the covered sites when some site is
// unavailable, and "Published, not traced" as the known-value sum whenever a covered site's count is unknown (the
// server's legacy Total would read 0 for "none known"). Unchanged when every site is covered and every count known.
export function liveTotal(total: TotalCounts, c: LiveCoverage): TotalCounts {
  return c.unavailable.length === 0 && c.notTracedMissing.length === 0 ? total : { ...total, ...c.counts };
}
const NO_COVERED_TITLE = 'No site with a readable manifest: not available, not zero';

export const isFeedNotMeasured = (row: Partial<SiteRow>) => row.feed_coverage === 'not_measured';
export function feedNotMeasuredTitle(row: Partial<SiteRow>): string {
  // Reasons: a non-file source (shopify_api), an import that did not stamp every product (incomplete_import),
  // or a feed filter the report cannot judge (unsupported_feed_filter:<config id>).
  const reasons = (row.feed_unmeasured_sources ?? []).join(', ') || 'unknown';
  return `Feed membership is not measured (${reasons}): not available, not zero. Left out of the totals.`;
}

// The feed the "In the feed" column reads: the latest successful feed import of the site.
export function feedImportLabel(row: Partial<SiteRow>): string {
  if (isFeedNotMeasured(row)) return `feed coverage not measured (${(row.feed_unmeasured_sources ?? []).join(', ')})`;
  if (row.feed_imported_at === undefined) return '';
  return row.feed_imported_at === null ? 'last feed import: never' : `last feed import ${utcShort(row.feed_imported_at)}`;
}

export function manifestLabel(state: ManifestState): string {
  switch (state.kind) {
    case 'read': case 'later_attempt': return `manifest read ${utcShort(state.readAt)}`;
    case 'none': return 'no manifest read yet';
    case 'no_id': return `manifest lines carry no product id (${state.lines.toLocaleString('en-US')})`;
    case 'unknown': return '';
  }
}

// A site with content delivery off, listed apart and outside the total. {id, name, reason} is
// all the report returned for it before the counts were added, and a backend not yet updated
// still sends only that: its counts are then shown as unavailable, never as zeros.
interface ExcludedSite extends Partial<SiteRow> {
  id: string;
  name: string;
  reason: string;
}

interface Report {
  status?: 'ready'; // absent from a backend older than #6808
  organization: { id: string; name: string; slug: string };
  generated_at: string;
  // The server never builds on the request (#6808): it answers with its last finished report.
  built_at?: string;
  stale?: boolean; // older than 10 minutes: a rebuild is running
  rebuilding?: boolean;
  last_build_error?: { message: string; at: string } | null; // the last rebuild failed; these figures are older
  // The server could not keep this report for its next restart (Redis); the figures themselves are fine.
  persistence_error?: { message: string; at: string; during: 'read' | 'write' } | null;
  since: string | null;
  sites: SiteRow[]; // content delivery on
  // Catalog columns: sum of `sites` whose feed coverage is measured. Records: every site of `sites`.
  // Live: the sites whose manifest is measured, with the bases of their percentages.
  total: TotalCounts;
  not_measured_sites?: { id: string; name: string; sources: string[] }[]; // outside the catalog total
  live_unavailable_sites?: { id: string; name: string; reason: string | null }[]; // outside the Live total
  excluded_sites: ExcludedSite[]; // content delivery off
}

// 202: no build of this (organization, since) has finished yet; one is running on the server.
// last_build_error: the previous attempt failed (the server keeps it until a build succeeds).
interface Building { status: 'building'; started_at: string; last_build_error?: { message: string; at: string } | null }

// While the server builds, poll at this pace instead of the 10-minute one.
const BUILD_POLL_MS = 15_000;

interface OrgListItem { id: string; name: string; site_count: number }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number | null | undefined, of: number | null | undefined): number | null {
  return n != null && of != null && of > 0 ? (100 * n) / of : null;
}
// A figure the server did not send (an older backend) or could not count: "—", never 0.
const fmtOr = (n: number | null | undefined) => (n === null || n === undefined ? '—' : fmt(n));

// Rounded DOWN: never shows 100% before the last product, and the label never
// crosses a colour threshold the value has not reached (97.99 reads 97%, amber).
export function pctLabel(p: number | null): string {
  if (p === null) return '—';
  if (p >= 100) return '100%';
  if (p >= 99 || p < 10) return `${(Math.floor(p * 10) / 10).toFixed(1)}%`;
  return `${Math.floor(p)}%`;
}

// Delivery tone: the target is 100% of the total, 24–48 h after a feed update.
export function tone(p: number | null): 'good' | 'warn' | 'bad' | 'none' {
  if (p === null) return 'none';
  if (p >= 98) return 'good';
  if (p >= 90) return 'warn';
  return 'bad';
}

const TONE_CLASS: Record<string, string> = {
  good: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900',
  warn: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
  bad: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900',
  none: 'bg-muted text-muted-foreground ring-border',
};

function PctPill({ value, neutral = false }: { value: number | null; neutral?: boolean }) {
  return (
    <span className={`inline-flex min-w-[3.25rem] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ring-1 ring-inset ${TONE_CLASS[neutral ? 'none' : tone(value)]}`}>
      {pctLabel(value)}
    </span>
  );
}

// ─── Column model ─────────────────────────────────────────────────────────────

type Key = keyof Counts;

interface Column {
  key: Key;
  label: string;
  help: string;
  base?: Key; // shows a % of this column, on the same row
  outside?: boolean; // a manifest-quality signal, drawn apart
  catalog?: boolean; // the catalog population; the records columns follow, drawn apart
}

export function columns(languages: number | null, withSince: boolean, sinceLabel: string): Column[] {
  const langs = languages ? `all ${languages} languages` : 'every target language';
  const cols: Column[] = [
    { key: 'in_feed', label: 'In the feed', catalog: true,
      help: 'Canonical products selected by this site\'s configured feed filter in your current feed: one per item group (item_group_id), counted when the latest successful import of your feed contained it. Products your current feed does not contain are not counted.' },
    { key: 'eligible_not_generated', label: 'Eligible, not generated', catalog: true,
      help: 'Canonical products this site\'s configured feed filter selects in your current feed, counted once per item_group_id, with no non-failed current generated page in the group. Products without an item group are counted separately. This is an output gap, not readiness to start or retry generation.' },
    { key: 'records_generated', label: 'Generated',
      help: 'Canonical products with a generated page in PageMind\'s records, whatever your feed or manifest says now: their own page (not failed), or a page the export serves them.' },
    { key: 'records_approved', label: 'Approved', base: 'records_generated',
      help: 'Generated products the export has an approved page for (its own, or the one the export serves it). Percentage of Generated.' },
    { key: 'records_ready', label: 'Ready for export', base: 'records_generated',
      help: `Approved and translated into ${langs} on the current version: what the export selects for delivery, before publication validation. On a governed site the export's publication guard can still refuse some of these at delivery, so Ready can exceed what is delivered; each refusal is recorded in the export ledger. This is eligibility, not proof that the merchant received or published it. Percentage of Generated.` },
    { key: 'records_live_latest', label: 'Live, latest version', base: 'records_ready',
      help: 'Ready products whose item group your publisher manifest reports published at or after the page\'s approval. From the merchant\'s publisher manifest. Every column before this one comes from PageMind\'s own records. Percentage of Ready for export.' },
    { key: 'records_live_any', label: 'Live, any version', base: 'records_generated',
      help: 'Generated products whose item group your current publisher manifest lists, possibly with an older version. From the merchant\'s publisher manifest. Every column before this one comes from PageMind\'s own records. Percentage of Generated (an older version can be live while the current translation is incomplete).' },
    { key: 'records_ready_not_live_latest', label: 'Ready, not live on latest',
      help: 'Ready for export, but the manifest does not report the latest version published: the adoption gap. From the merchant\'s publisher manifest.' },
    { key: 'published_not_traced', label: 'Published, not traced', outside: true,
      help: 'Item groups your current manifest file lists that match no product in your current feed or linked by your manifest. A manifest-quality signal.' },
  ];
  if (withSince) {
    cols.push(
      { key: 'records_generated_since', label: `Generated since ${sinceLabel}`, help: `Generated products whose page was created on or after ${sinceLabel} (UTC).` },
      { key: 'records_approved_since', label: `Approved since ${sinceLabel}`, help: `Approved products whose served page was approved on or after ${sinceLabel} (UTC).` },
    );
  }
  return cols;
}

// Same order as the screen: sites with content delivery on, their Total, then the others.
// The Manifest column carries what the screen prints under each site; a figure that is not
// available (catalog of a not-measured feed, Live without a readable manifest, Ready without a
// language) exports blank, never 0, and the Total says what its Live figures cover.
type LiveBases = Partial<Record<Key, number | null | undefined>>;
export function liveBases(total: TotalCounts, c: LiveCoverage): LiveBases {
  // The server's bases cover exactly the sites its Live sums cover; an older backend's are derived the same way.
  return {
    records_generated: total.records_live_base_generated ?? c.base.records_generated,
    records_ready: total.records_live_base_ready ?? c.base.records_ready,
  };
}
export function toCsv(report: Report, cols: Column[]): string {
  const head = ['Site', 'Content delivery', 'Manifest', ...cols.flatMap((c) => (c.base ? [c.label, `${c.label} %`] : [c.label]))];
  const line = (name: string, delivery: string, manifest: string, r: Partial<TotalCounts>, opts: { liveUnavailable: boolean; bases?: LiveBases }) =>
    [name, delivery, manifest, ...cols.flatMap((c) => {
      const raw = r[c.key];
      const live = LIVE_KEYS.includes(c.key);
      if (raw === null || raw === undefined || (live && opts.liveUnavailable)) return c.base ? ['', ''] : [''];
      const v = Number(raw);
      if (!c.base) return [String(v)];
      const of = live && opts.bases ? opts.bases[c.base] : r[c.base];
      const p = pct(v, of === null || of === undefined ? null : Number(of));
      // Like the UI, never round an incomplete scope up to 100%.
      return [String(v), p === null ? '' : (Math.floor(p * 10) / 10).toFixed(1)];
    })];
  const siteLine = (s: Partial<SiteRow>, delivery: string) => {
    const state = manifestState(s);
    const idless = state.kind !== 'no_id' && (s.manifest_lines_without_id ?? 0) > 0 ? `${s.manifest_lines_without_id} lines without id, not matched` : '';
    const notMeasured = isFeedNotMeasured(s) ? feedImportLabel(s) : '';
    return line(s.site_name ?? (s as ExcludedSite).name, delivery, [notMeasured, manifestLabel(state), laterAttemptLabel(state), idless].filter(Boolean).join('; '),
      s, { liveUnavailable: liveUnavailableSite(s) });
  };
  const coverage = liveCoverage(report.sites);
  const rows = [
    head,
    ...report.sites.map((s) => siteLine(s, 'on')),
    line('Total (content delivery on)', '', coverageLabel(coverage), liveTotal(report.total, coverage),
      { liveUnavailable: coverage.unavailable.length > 0 && coverage.covered === 0, bases: liveBases(report.total, coverage) }),
    ...report.excluded_sites.map((s) => siteLine(s, 'off')),
  ];
  return csvText(rows);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrgContentDelivery() {
  const { selectedSite, sites, loading: sitesLoading } = useSite();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const queryClient = useQueryClient();

  const orgs = useQuery<OrgListItem[]>({
    queryKey: ['organizations', 'list'],
    queryFn: () => getJson<OrgListItem[]>(`${API_URL}/organizations`),
    staleTime: 5 * 60_000,
  });

  // The organization shown: the one the user picked, else a default DERIVED from the selected
  // site (never stored), so a site restored after the org list arrived still wins.
  // The site's organization is read from the FRESH sites list, not from selectedSite: a site
  // restored from localStorage may predate organization_id. The default waits only for the
  // site list and, while an active site exists to be selected, for the selection. A member who
  // sees no site, or no active one, gets the largest organization.
  const [pickedOrgId, setPickedOrgId] = useState<string | undefined>();
  const [since, setSince] = useState('');
  const [tab, setTab] = useState('delivery');
  const selectedId = selectedSite ? (selectedSite.id || selectedSite._id) : undefined;
  const freshSelected = selectedId ? (sites.find((s) => (s.id || s._id) === selectedId) ?? selectedSite) : null;
  // SiteContext auto-selects only an ACTIVE site, so with no active site nothing will ever be
  // selected: stop waiting then too. Every other path ends with a selection or an empty list
  // (the selector's fetch clears `loading` in a finally, success or failure).
  const selectionPending = freshSelected === null && sites.some((s) => s && s.status === 'active');
  const siteReady = (!sitesLoading || sites.length > 0) && !selectionPending;
  const siteOrgId = freshSelected?.organization_id ?? null;
  const defaultOrgId = useMemo(() => {
    if (!siteReady || !orgs.data?.length) return undefined;
    const ofSite = orgs.data.find((o) => o.id === siteOrgId);
    const largest = [...orgs.data].sort((a, b) => b.site_count - a.site_count)[0];
    return (ofSite || largest).id;
  }, [siteReady, orgs.data, siteOrgId]);
  const orgId = pickedOrgId ?? defaultOrgId;

  const sinceValid = since === '' || /^\d{4}-\d{2}-\d{2}$/.test(since);
  const report = useQuery<Report | Building>({
    queryKey: ['organizations', orgId, 'content-delivery', since],
    queryFn: () => getJson<Report | Building>(`${API_URL}/organizations/${orgId}/content-delivery${since ? `?since=${since}` : ''}`),
    enabled: !!orgId && sinceValid,
    staleTime: 5 * 60_000,
    // The server rebuilds a report every 10 minutes; poll at the same pace so a page left open
    // keeps the promise its footer makes, and faster while a build is running.
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && (d.status === 'building' || (d as Report).rebuilding) ? BUILD_POLL_MS : REFRESH_MS;
    },
  });

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  async function forceRefresh() {
    if (!orgId) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      // Starts a rebuild on the server and answers with the current report at once; the page then polls.
      const fresh = await getJson<Report | Building>(`${API_URL}/organizations/${orgId}/content-delivery?refresh=1${since ? `&since=${since}` : ''}`);
      queryClient.setQueryData(['organizations', orgId, 'content-delivery', since], fresh);
    } catch (err) {
      setRefreshError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  const building = report.data?.status === 'building' ? (report.data as Building) : null;
  const data = building ? undefined : (report.data as Report | undefined);
  const builtAt = data ? (data.built_at || data.generated_at) : null;
  const activeSites = data?.sites || [];
  const offSites = data?.excluded_sites || [];
  const languages = useMemo(() => {
    const counts = new Set((data?.sites || []).map((s) => s.target_language_count));
    return counts.size === 1 ? [...counts][0] : null;
  }, [data]);
  const sinceLabel = data?.since
    ? new Date(data.since).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    : '';
  const cols = columns(languages, !!data?.since, sinceLabel);
  const variantSites = activeSites.filter((s) => !s.canonical_mode);
  const notMeasuredSites = activeSites.filter(isFeedNotMeasured);
  const offWithoutCounts = offSites.filter((s) => s.in_feed === undefined);
  const noManifestSites = activeSites.filter((s) => manifestState(s).kind === 'none');
  const noIdSites = activeSites.filter((s) => manifestState(s).kind === 'no_id');
  const laterAttemptSites = activeSites.filter((s) => manifestState(s).kind === 'later_attempt');
  const withoutPageSites = activeSites.filter((s) => (s.published_without_page ?? 0) > 0);
  const staleSourceSites = activeSites.filter((s) => (s.manifest_stale_sources ?? 0) > 0);
  // Some lines carry no id while others do: the file is read and "Live" is available, but
  // those lines match nothing — said, never silent.
  const someIdlessSites = activeSites.filter((s) => manifestState(s).kind !== 'no_id' && (s.manifest_lines_without_id ?? 0) > 0);
  const coverage = liveCoverage(activeSites);
  const livePartial = coverage.unavailable.length > 0;
  const noneCovered = livePartial && coverage.covered === 0;
  const bases = data ? liveBases(data.total, coverage) : {};
  const total = data ? liveTotal(data.total, coverage) : null;
  const lastCatalogKey = cols.filter((c) => c.catalog).slice(-1)[0]?.key;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="p-6 max-w-[1440px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Content delivery</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              What your current feed asks for, and everything PageMind has made and delivered for {data?.organization.name || 'the organization'}:
              generated, approved, ready for export, then confirmed live by your publisher manifest.
            </p>
          </div>
          {(orgs.data?.length || 0) > 1 && (
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Organization
              <Select value={orgId} onValueChange={setPickedOrgId}>
                <SelectTrigger className="w-56 h-9"><SelectValue placeholder="Select an organization" /></SelectTrigger>
                <SelectContent>
                  {orgs.data!.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name} ({o.site_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
        </div>

        {orgs.error && (
          <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Could not load your organizations: {(orgs.error as Error).message}
          </div>
        )}

        {orgs.data && orgs.data.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">Your account is not a member of any organization.</div>
        )}

        {!(orgs.data && orgs.data.length === 0) && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="delivery">Delivery</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="monthly">Monthly usage</TabsTrigger>
            </TabsList>

            {/* Radix unmounts the inactive tab: the activity is only fetched once its tab is opened. */}
            <TabsContent value="activity" className="mt-4">
              {orgId ? <DeliveryDailyActivity key={orgId} orgId={orgId} /> : <p role="status">Waiting for organization selection…</p>}
            </TabsContent>

            <TabsContent value="monthly" className="mt-4">{orgId ? <DeliveryMonthlyUsage key={orgId} orgId={orgId} /> : <p role="status">Waiting for organization selection…</p>}</TabsContent>

            <TabsContent value="delivery" className="mt-4 space-y-6">
              <div className="flex items-end justify-end gap-2 flex-wrap">
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Since
                  <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="w-40 h-9" aria-label="Generated and approved since" />
                </label>
                {since && <Button variant="ghost" size="sm" className="h-9" onClick={() => setSince('')}>Clear</Button>}
                {isSuperAdmin && (
                  <Button variant="outline" size="sm" className="h-9" onClick={forceRefresh} disabled={!data || refreshing}>
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-9" disabled={!data}
                  onClick={() => data && download(`${data.organization.slug}-content-delivery-${data.generated_at.slice(0, 10)}.csv`, toCsv(data, cols))}>
                  <Download className="h-4 w-4 mr-1.5" /> Export CSV
                </Button>
              </div>

              {refreshError && (
                <div role="alert" className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Refresh failed: {refreshError}. The figures below are from {builtAt ? formatDistanceToNow(new Date(builtAt), { addSuffix: true }) : 'the last load'}.
                </div>
              )}

              {report.error && (
                <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Could not load the content delivery report: {(report.error as Error).message}
                </div>
              )}

              {data?.last_build_error && (
                <div role="alert" className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  The last update failed {formatDistanceToNow(new Date(data.last_build_error.at), { addSuffix: true })}: {data.last_build_error.message}. The figures below are from {formatDistanceToNow(new Date(builtAt!), { addSuffix: true })}.
                </div>
              )}

              {data?.persistence_error && (
                <p role="status" className="flex items-center gap-2 text-xs text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  These figures could not be saved for the server's next restart ({data.persistence_error.message}). After a restart the report will be rebuilt from scratch.
                </p>
              )}

              {data?.rebuilding && (
                <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Updating the figures. The ones below are from {formatDistanceToNow(new Date(builtAt!), { addSuffix: true })}.
                </p>
              )}

              {building?.last_build_error && (
                <div role="alert" className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  The previous attempt failed {formatDistanceToNow(new Date(building.last_build_error.at), { addSuffix: true })}: {building.last_build_error.message}. Trying again.
                </div>
              )}

              {(orgs.isLoading || report.isLoading || (building && !report.error)) && (
                <div className="space-y-4" aria-busy="true">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
                  <Skeleton className="h-96 rounded-xl" />
                  <p role="status" className="text-xs text-muted-foreground">
                    {building
                      ? `Counting every product of every site for the first time (started ${formatDistanceToNow(new Date(building.started_at), { addSuffix: true })}). This can take several minutes; the page updates by itself.`
                      : 'Loading the report…'}
                  </p>
                </div>
              )}

              {data && (
                <>
                  <p className="text-sm text-muted-foreground">
                    “In the feed” and “Eligible, not generated” describe your current feed. Generated, Approved and Ready for export count PageMind's own records
                    for every canonical product, whatever the feed says now; Live comes from your publisher manifest. Monthly usage shows billed generation history; the totals need not match.
                  </p>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <SummaryTile label="Generated" value={fmtOr(data.total.records_generated)}
                      sub={`${fmtOr(data.total.in_feed)} in the feed${notMeasuredSites.length ? ` (${notMeasuredSites.length} ${notMeasuredSites.length === 1 ? 'site' : 'sites'} not measured)` : ''}`} />
                    <SummaryTile label="Approved" value={pctLabel(pct(data.total.records_approved, data.total.records_generated))}
                      sub={`${fmtOr(data.total.records_approved)} of ${fmtOr(data.total.records_generated)} generated`} tone={tone(pct(data.total.records_approved, data.total.records_generated))} />
                    <SummaryTile label="Ready for export" value={pctLabel(pct(data.total.records_ready, data.total.records_generated))}
                      sub={`${fmtOr(data.total.records_ready)} selected by the export, not proof of delivery`} tone={tone(pct(data.total.records_ready, data.total.records_generated))} />
                    <SummaryTile label="Live, latest version" value={noneCovered ? '—' : pctLabel(pct(total!.records_live_latest, bases.records_ready))}
                      sub={noneCovered ? 'no site with a readable manifest'
                        : `${fmtOr(total!.records_live_latest)} of ${fmtOr(bases.records_ready)} ready, confirmed by your manifest${livePartial ? ` · covers ${coverage.covered} of ${coverage.sites} sites` : ''}`}
                      tone={noneCovered ? 'none' : tone(pct(total!.records_live_latest, bases.records_ready))} />
                  </div>

                  {/* Table */}
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted text-left align-bottom">
                              <th className="px-4 py-3 font-semibold sticky left-0 z-10 bg-muted">Site</th>
                              {cols.map((c) => (
                                <th key={c.key} className={`px-3 py-3 font-semibold text-right ${c.key === lastCatalogKey ? 'border-r' : ''} ${c.outside ? 'border-l text-muted-foreground' : ''}`}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help underline decoration-dotted decoration-muted-foreground/60 underline-offset-4">{c.label}</span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs text-xs leading-relaxed">{c.help}</TooltipContent>
                                  </Tooltip>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {activeSites.map((s) => (
                              <Row key={s.site_id} name={s.site_name} sub={s.domain || undefined} status={s.status} row={s} cols={cols} lastCatalogKey={lastCatalogKey} />
                            ))}
                          </tbody>
                          <tbody>
                            <Row name="Total" row={total!} cols={cols} strong coverage={coverage} bases={bases} lastCatalogKey={lastCatalogKey}
                              sub={offSites.length > 0 ? `${activeSites.length} ${activeSites.length === 1 ? 'site' : 'sites'} with content delivery on` : undefined} />
                          </tbody>
                          {offSites.length > 0 && (
                            <tbody>
                              <tr>
                                <td colSpan={cols.length + 1} className="px-4 pt-6 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  Content delivery not activated · not in the total
                                </td>
                              </tr>
                              {offSites.map((s) => (
                                <Row key={s.site_id ?? s.id} name={s.site_name ?? s.name} sub={s.domain || undefined} status={s.status} row={s} cols={cols} lastCatalogKey={lastCatalogKey} off />
                              ))}
                            </tbody>
                          )}
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Notes */}
                  <div className="text-xs text-muted-foreground space-y-1.5 max-w-4xl leading-relaxed">
                    <p>
                      Updated {formatDistanceToNow(new Date(builtAt!), { addSuffix: true })}. Figures refresh every 10 minutes.
                      Approved, Ready for export and Live, any version are percentages of Generated; Live, latest version is a percentage of Ready for export.
                      After a product becomes ready, it normally reaches “Live, latest version” within 24 to 48 hours.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      “Eligible, not generated” counts canonical products the site's configured feed filter selects in the current feed, once per item group, without a current generated page. Products without an item group count separately. This does not mean all of them can start generating now.
                    </p>
                    <p>
                      Products are counted once per canonical product: one per item group (the feed's item_group_id), not per size or colour.
                      “Live” comes from the publisher manifest your website sends us every day: a product is live when the file last read
                      lists its item group, and the date of that read is printed under each site. Products delivered after it cannot show as live yet.
                      “Published, not traced” counts item groups the file lists that match no product in your current feed or linked by your manifest;
                      lines without any id count nowhere.
                    </p>
                    {noManifestSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        No manifest file has been read yet for {noManifestSites.map((s) => s.site_name).join(', ')}: their “Live” columns are not available, not zero.
                      </p>
                    )}
                    {noIdSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        The manifest lines of {noIdSites.map((s) => s.site_name).join(', ')} carry no item group and no product id, so nothing can be matched:
                        check the manifest config's column mapping and URL pattern. Their “Live” columns are not available, not zero.
                      </p>
                    )}
                    {laterAttemptSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        Another manifest read of {laterAttemptSites.map((s) => s.site_name).join(', ')} has not finished, failed or overlapped the last successful one:
                        their “Live” figures use that read, dated under each site, and may include lines that run changed.
                      </p>
                    )}
                    {withoutPageSites.length > 0 && (
                      <p>
                        The manifest lists products of your current feed or manifest that we never generated, so they are not counted as live:{' '}
                        {withoutPageSites.map((s) => `${s.site_name} ${fmt(s.published_without_page ?? 0)}`).join(', ')}.
                      </p>
                    )}
                    {someIdlessSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        Some manifest lines carry no item group and no product id, so they could not be matched to any product and count nowhere:{' '}
                        {someIdlessSites.map((s) => `${s.site_name} ${fmt(s.manifest_lines_without_id ?? 0)}`).join(', ')}. Check the manifest config's column mapping and URL pattern.
                      </p>
                    )}
                    {staleSourceSites.length > 0 && (
                      <p>
                        A manifest source not read within a day of the site's latest read is ignored (its file is old):{' '}
                        {staleSourceSites.map((s) => `${s.site_name} ${s.manifest_stale_sources}`).join(', ')}.
                      </p>
                    )}
                    {livePartial && (
                      <p className="text-amber-700 dark:text-amber-400">
                        The Total's “Live” figures are partial: they cover {coverage.covered} of {coverage.sites} sites, and their percentages are of those sites'
                        Generated ({fmtOr(bases.records_generated)}) and Ready for export ({fmtOr(bases.records_ready)}), not of the whole Total.
                      </p>
                    )}
                    {offSites.length > 0 && (
                      <p>
                        Content delivery is not activated on {offSites.map((s) => s.site_name ?? s.name).join(', ')}: no target language is set, so no content is delivered.
                        Their products and any pages already generated are shown for reference, below the total and outside it.
                      </p>
                    )}
                    {offWithoutCounts.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        The server did not report counts for {offWithoutCounts.map((s) => s.name).join(', ')}: they are shown as “—”.
                      </p>
                    )}
                    {notMeasuredSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        Feed membership is not measured for {notMeasuredSites.map((s) => `${s.site_name} (${(s.feed_unmeasured_sources ?? []).join(', ')})`).join(', ')}:
                        their “In the feed” and “Eligible, not generated” are shown as “—” and left out of those totals. Their records columns are counted.
                      </p>
                    )}
                    {variantSites.length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        Counted per variant, not per canonical product: {variantSites.map((s) => s.site_name).join(', ')}.
                      </p>
                    )}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </TooltipProvider>
  );
}

function Row({ name, sub, status, row, cols, strong = false, off = false, coverage, bases, lastCatalogKey }: {
  name: string; sub?: string; status?: string | null; row: Partial<TotalCounts & SiteRow>; cols: Column[]; strong?: boolean; off?: boolean;
  coverage?: LiveCoverage; bases?: LiveBases; lastCatalogKey?: Key;
}) {
  // The Total row carries no manifest_read_at: its state is 'unknown' and nothing is printed for
  // it; what its Live columns cover comes from the sites (coverage).
  const manifest = strong ? { kind: 'unknown' as const } : manifestState(row);
  const livePartial = Boolean(coverage && coverage.unavailable.length > 0);
  const noneCovered = Boolean(livePartial && coverage && coverage.covered === 0);
  const liveUnavailable = (strong ? false : liveUnavailableSite(row)) || noneCovered;
  const laterAttempt = laterAttemptLabel(manifest);
  const liveTitle = noneCovered ? NO_COVERED_TITLE : liveUnavailableTitle(manifest) || 'The manifest is not measured for this site: not available, not zero';
  const nullTitle = (c: Column) => (CATALOG_KEYS.includes(c.key) || c.key === 'published_not_traced') && isFeedNotMeasured(row) ? feedNotMeasuredTitle(row)
    : strong && c.key === 'published_not_traced' && coverage && coverage.notTracedMissing.length > 0 ? `${coverageLabel(coverage)}: not available, not zero`
    : c.key === 'records_ready' || c.key === 'records_live_latest' || c.key === 'records_ready_not_live_latest' ? 'No target language: nothing to translate into'
      : c.key === 'eligible_not_generated' ? 'Eligibility unavailable' : 'Not available, not zero';
  return (
    <tr className={strong ? 'border-t-2 border-b-2 bg-muted font-semibold' : `group border-b last:border-b-0 ${off ? 'text-muted-foreground' : ''}`}>
      <td className={`px-4 py-2.5 sticky left-0 z-10 ${strong ? 'bg-muted' : 'bg-card'}`}>
        <div className="flex items-center gap-1.5">
          {name}
          {status && status !== 'active' && (
            <span className="rounded bg-muted px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground ring-1 ring-inset ring-border">{status}</span>
          )}
        </div>
        {sub && <div className="text-[11px] text-muted-foreground font-normal">{sub}</div>}
        {!strong && feedImportLabel(row) && (
          <div className={`text-[11px] font-normal ${isFeedNotMeasured(row) ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{feedImportLabel(row)}</div>
        )}
        {manifest.kind !== 'unknown' && (
          <div className={`text-[11px] font-normal ${liveUnavailable ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{manifestLabel(manifest)}</div>
        )}
        {laterAttempt && <div className="text-[11px] font-normal text-amber-700 dark:text-amber-400">{laterAttempt}</div>}
      </td>
      {cols.map((c) => {
        const raw = row[c.key];
        const v = Number(raw ?? 0);
        const live = LIVE_KEYS.includes(c.key);
        const of = c.base ? (live && strong && bases ? bases[c.base] : row[c.base]) : null;
        return (
          <td key={c.key} className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${c.key === lastCatalogKey ? 'border-r' : ''} ${c.outside ? 'border-l text-muted-foreground' : ''}`}>
            {liveUnavailable && live ? (
              <span title={liveTitle}>—</span>
            ) : raw === null ? (
              <span title={nullTitle(c)}>—</span>
            ) : raw === undefined ? (
              <span title="Not reported by the server">—</span>
            ) : (
              <div className="flex items-center justify-end gap-2">
                {live && coverage && (livePartial || (strong && c.key === 'published_not_traced' && coverage.notTracedMissing.length > 0)) && (
                  <span title={`${coverageLabel(coverage)}. Percentage of the covered sites only.`}
                    className="rounded bg-amber-50 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">partial</span>
                )}
                <span>{fmt(v)}</span>
                {c.base && <PctPill value={pct(v, of === null || of === undefined ? null : Number(of))} neutral={off} />}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}
